import React, { useMemo, useState, useContext, Suspense } from 'react';

import { dashboardBookingInquiryApis } from '@apis/next/bookingInquiryApis';
import lockIcon from '@assets/img/icons/lock.svg';
import unlockIcon from '@assets/img/icons/lock.svg';
import { BookingDetailsContext } from '@components/admin/booking/helpers';
import { TextInput } from '@components/admin/Forms';
import { VesselScheduleFilter } from '@components/admin/widgets/VesselScheduleFilter';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogContent,
  ActionableDialogTitle,
} from '@components/Dialog';
import { LogForm } from '@components/Form';
import { FormItem } from '@components/Forms/FormItem';
import { makeFormItemProps } from '@components/Forms/helpers';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { SpinnerModal } from '@components/SpinnerModal';
import { bookingInquiryTradeConditionStrings } from '@data/bookingInquiry';
import { usePatchPreAlert } from '@hooks/mutation/dashboard/usePatchPreAlert';
import { usePutPreAlertCreate } from '@hooks/mutation/dashboard/usePutPreAlertCreate';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { AdminInquiryDetailHelper, tradeCondtion } from '@models/BookingInquiry';
import type { PreAlertDetails } from '@models/next/PreAlert';
import { PACKAGES } from '@models/PreAlert';
import { useCommonStyles, makeMuiStyles } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { foldMessagesEither } from '@utils/errors';
import {
  requiredPositiveIntegerField,
  requiredStringField,
  createValidate,
  stringNumberToThirdDecimalPlaceField,
  formStrings,
  stringField,
} from '@utils/forms';
import { createStrings } from '@utils/local';
import { id, toJST } from '@utils/types';
import clsx from 'clsx';
import Color from 'color';
import dayjs from 'dayjs';
import { Formik, Form, useField, useFormikContext } from 'formik';
import * as t from 'io-ts';

const PreAlertFormContext = React.createContext<{
  edit: boolean;
  onEdit: PreAlertFormProps['onEdit'];
}>({
  edit: false,
} as any);

const CarrierAutoComplete = React.lazy(() => import('@components/Forms/CarrierAutoComplete'));

const strings = createStrings({
  ja: {
    edit: '編集',
    create: '作成',
    update: '更新',
    export: 'エクスポート',
    history: '変更履歴',
    customerNo: 'お客様の管理番号',
    lock: 'LOCK',
    unlock: 'UNLOCK',
    lockPreAlert: 'Pre Alertをロックする',
    bookingWillBeMovedToAfterDepart:
      'Pre Alert上锁后，该案件会被移到“進行中の案件（出港後）”页面。',
    vesselScheduleEtdAlert: () => (
      <>
        荷受倉庫への搬入予定日が変更されましたので、PRE
        ALERTのVesselスケジュールを再度確認してください。
        <br />
        因荷受仓库的搬入预定日产生变更，请重新选择PRE ALERT的船期信息。
      </>
    ),
    vesselScheduleVesselScheduleAlert: () => (
      <>
        Vesselスケジュールを指定してください。
        <br />
        请指定一个船期。
      </>
    ),
    unlockAlert:
      'Pre Alertのロックが解除されると、Pre Alertのデータが変更されても、この変更はSea WaybillとArrival Noticeに反映されません。ご了承ください。',
    unableToUnlock: () => (
      <>
        申し訳ありませんが、あなたのアカウントにはPre Alertのロックを解除する権限がありません。
        <br />
        Pre Alertのロックを解除する必要がある場合は、管理者に連絡してください。
        <br />
        <br />
        Pre Alertのロックが解除されると、Pre Alertのデータが変更されても、この変更はSea
        WaybillとArrival Noticeに反映されません。ご了承ください。
        <br />
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  field: {
    ...common.flexInline,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: '1rem',
    padding: '0.5rem 3rem',
    '&:hover': {
      backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    },
  },
  promptField: {
    padding: '0.5rem 3rem',
  },
  label: {
    ...common.middleText,
    width: '10rem',
    color: theme.palette.grey[700],
    textAlign: 'left',
    paddingTop: 5,
  },
  hidden: {
    visibility: 'hidden',
  },
  textInputReadOnly: {
    ...common.middleText,
    ...common.wordBreakBreakAll,
    color: theme.palette.grey[700],
    padding: '5px 5px',
    '&&&': {
      backgroundColor: 'transparent',
      border: 'none',
      '& > input': {
        backgroundColor: 'transparent',
      },
      /** for options */
      '& > input + img': {
        display: 'none !important',
      },
    },
  },
  flex: {
    flex: 1,
    minWidth: 0,
  },
  unitPostfix: {
    width: '4rem',
    ...common.middleText,
  },
  multilineInput: {
    width: 600,
  },
  multilineDisplay: {
    ...common.middleText,
    padding: '5px 5px',
    whiteSpace: 'pre-wrap',
    overflow: 'hidden',
    overflowWrap: 'break-word',
    color: theme.palette.grey[700],
  },
  updatedAt: {
    ...common.middleText,
    paddingLeft: '3rem',
    marginTop: '2rem',
    marginBottom: '2rem',
  },
  disabledButton: {
    color: theme.palette.grey[300],
    cursor: 'default',
  },
  editableLabel: {
    ...common.flexInline,
    ...common.justifyBetween,
  },
  dialog: {
    maxWidth: 'initial !important',
    width: 618,
  },
  contactDisplay: {
    display: 'grid',
    columnGap: '1rem',
    gridTemplateColumns: '1fr 1fr',
    '& > div': {
      ...common.preEllipsis,
    },
  },
  contactCard: {
    ...common.middleText,
    ...common.textGray900,
    ...common.flexColumn,
    rowGap: '0.5rem',
    padding: '0.5rem 1rem',
    border: `1px solid ${theme.palette.grey[300]}`,
    marginRight: '12rem',
  },
  contactList: {
    width: '100%',
    minWidth: '20rem',
    ...common.flexColumn,
    rowGap: '0.5rem',
  },
  alertPrimary: {
    ...common.smallerText,
    padding: 10,
    width: '100%',
    border: `1px solid ${theme.palette.primary.dark}`,
    backgroundColor: Color(theme.palette.primary.dark).alpha(0.1).toString(),
  },
}));

interface FieldProps extends React.ComponentProps<typeof TextInput> {
  label: string;
  labelNode?: React.ReactNode;
  name: keyof PreAlertDetails;
  displayOnly?: boolean;
  unit?: string;
  /**
   * A vessel already sailed
   */
  locked?: boolean;
  hasEditButton?: boolean;
  index: number;
  mapDisplayValue?: (v: any) => string;
}

const Field: React.FC<FieldProps> = (props) => {
  const classes = { ...useCommonStyles(), ...useStyles() };
  const {
    name,
    label,
    type,
    displayOnly = false,
    unit,
    multiline,
    labelNode,
    hasEditButton = true,
    index,
    locked,
    choose,
    options,
    mapDisplayValue = id,
    ...rest
  } = props;

  const { confirm, renderDialog } = useConfirmDialog();

  const { bookingInquiry, preAlert, user } = useContext(BookingDetailsContext);
  const { edit, onEdit } = useContext(PreAlertFormContext);
  const readOnly = edit || displayOnly;

  const [field] = useField(name);

  const [textInputElement, setTextInputElement] = useState<HTMLInputElement>();

  const displayedText = mapDisplayValue(field.value) as string;

  const renderTextInput = () => (
    <TextInput
      name={name}
      label={labelNode ?? `${index}. ${label}`}
      type={type ?? 'text'}
      classes={{
        textInput: clsx(multiline && classes.multilineInput),
      }}
      formClasses={{ label: classes.label, root: classes.flex }}
      multiline={multiline}
      choose={choose}
      options={options}
      onFocus={(e: any) => {
        setTextInputElement(e.target);
      }}
      {...rest}
    />
  );

  const renderTextDisplay = () => (
    <FormItem
      {...makeFormItemProps({
        name,
        label: labelNode ?? `${index}. ${label}`,
        formClasses: {
          root: classes.flex,
          label: classes.label,
        },
      })}
    >
      <div className={classes.textInputReadOnly}>
        {choose ? (
          options?.find((option) => {
            if ('value' in option) {
              return option.value === field.value;
            }
          })?.label
        ) : (
          <>
            {displayedText || '-'}
            {unit ? ` ${unit}` : ''}
          </>
        )}
      </div>
    </FormItem>
  );

  const renderMultilineDisplay = () => (
    <FormItem
      {...makeFormItemProps({
        name,
        label: `${index}. ${label}`,
        formClasses: {
          root: classes.flex,
          label: classes.label,
        },
      })}
    >
      <div className={classes.multilineDisplay}>{displayedText}</div>
    </FormItem>
  );

  return (
    <>
      {renderDialog()}

      {!multiline && readOnly && renderTextDisplay()}
      {multiline && readOnly && renderMultilineDisplay()}
      {!readOnly && renderTextInput()}

      {name === 'carrier' && (
        <Suspense fallback={null}>
          <CarrierAutoComplete
            name={name}
            anchor={textInputElement}
            onClose={() => setTextInputElement(undefined)}
          />
        </Suspense>
      )}

      <span style={{ width: '4rem' }} className={classes.unitPostfix}>
        {!readOnly && unit}
      </span>
      <div
        style={{ width: '6rem', height: 32 }}
        className={clsx((!hasEditButton || !edit) && classes.hidden, classes.flexCenter)}
      >
        <a
          className={clsx(
            classes.link,
            classes.middleText,
            classes.baseButton,
            locked && classes.disabledButton,
          )}
          onClick={() => {
            if (locked) return;
            foldMessagesEither(
              new AdminInquiryDetailHelper(bookingInquiry).canEdit(user, preAlert),
              ({ messages }) => {
                confirm({ message: messages });
              },
              () => {
                onEdit(name, label);
              },
            );
          }}
        >
          {strings.edit}
        </a>
      </div>
    </>
  );
};

const WithPackages: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props;

  if (!React.isValidElement(children)) {
    return null;
  }

  return React.cloneElement<any, any>(children as any, {
    withAutoComplete: true,
    autoCompleteChoices: PACKAGES,
  });
};

const WithPaymentsTerms: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props;

  if (!React.isValidElement(children)) {
    return null;
  }

  return React.cloneElement<any, any>(children as any, {
    options: [...tradeCondtion.types.values()].map((literal) => ({
      label: bookingInquiryTradeConditionStrings[literal.value],
      value: literal.value,
    })),
    choose: true,
  });
};

interface VesselScheduleInputProps {
  name: string;
  open: boolean;
  onClose: () => void;
}

const VesselScheduleInput: React.FC<VesselScheduleInputProps> = (props) => {
  const { name, open, onClose } = props;

  const { bookingInquiry } = useContext(BookingDetailsContext);

  const formik = useFormikContext();
  const [field, , helpers] = useField<number | null>(name);

  const { edit } = useContext(PreAlertFormContext);

  const patchPreAlert = usePatchPreAlert(bookingInquiry.d2d_no);

  return (
    <VesselScheduleFilter
      open={open}
      onClose={onClose}
      selectedId={field.value ?? null}
      onChangeSelectedItem={async (item) => {
        helpers.setValue(item.id);
        formik.setFieldValue('vessel', item.vessel);
        formik.setFieldValue('voyage', item.voyage);
        formik.setFieldValue('pod', item.pod);
        formik.setFieldValue('pol', item.pol);
        formik.setFieldValue('eta', item.eta);
        formik.setFieldValue('etd', item.etd);

        if (edit) {
          await patchPreAlert.mutateAsync({
            detail: '-',
            vessel_schedule: item.id,
          } as any);
        }
      }}
    />
  );
};

export interface PreAlertFormProps {
  /**
   * Whether each field can edit
   */
  edit: boolean;
  onEdit: (name: keyof PreAlertDetails, label: string) => any;
  onExportClick: () => any;
  onHistoryClick: () => any;
}

export const PreAlertForm: React.FC<PreAlertFormProps> = (props) => {
  const classes = { ...useCommonStyles(), ...useStyles() };
  const { edit, onEdit, onExportClick, onHistoryClick } = props;

  const { bookingInquiry, preAlert, user } = useContext(BookingDetailsContext);

  const [vesselScheduleFilterOpen, setVesselScheduleFilterOpen] = useState(false);
  const [unlockAlertOpen, setUnlockAlertOpen] = useState(false);
  const [unableToUnlockAlertOpen, setUnableToUnlockAlertOpen] = useState(false);
  const updateAlert = useConfirmDialog();
  const bookingWillBeMovedAlert = useConfirmDialog();

  const putPreAlertCreate = usePutPreAlertCreate(bookingInquiry.id, bookingInquiry.d2d_no);
  const patchPreAlert = usePatchPreAlert(bookingInquiry.d2d_no);

  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries(
      dashboardBookingInquiryApis.getKeyByPath(
        'get',
        '/dashboard/booking-inquiry/:d2d_no/status-flows',
      ),
    );
  };

  const minimumEtd = preAlert.minimum_etd;
  const isVesselScheduleEtdInvalid = !!minimumEtd && !!preAlert.etd && preAlert.etd < minimumEtd;

  const formValuesType = useMemo(
    () =>
      t.type({
        refer_number: stringField('Refer Number'),
        shipper: requiredStringField('Shipper'),
        consignee: requiredStringField('Consignee'),
        house_bl_number: requiredStringField('House BL Number'),
        carrier: stringField('Carrier'),
        vessel: requiredStringField('Vessel'),
        voyage: requiredStringField('Voyage'),
        pol: requiredStringField('POL'),
        pod: requiredStringField('POD'),
        etd: requiredStringField('ETD'),
        eta: requiredStringField('ETA'),
        quantity: requiredPositiveIntegerField('Quantity'),
        packages: requiredStringField('Packages'),
        shipping_mark: requiredStringField('Shipper Mark', 1, 1024),
        commodity: requiredStringField('Commodity', 1, 512),
        gross_weight: stringNumberToThirdDecimalPlaceField('Gross Weight'),
        measurement: stringNumberToThirdDecimalPlaceField('Measurement'),
        payments_terms: requiredStringField('PaymentsTerms'),
        vessel_schedule: requiredPositiveIntegerField('Vessel Schedule'),
      }),
    [],
  );

  const validate = useMemo(() => createValidate(formValuesType), []);

  const locked = preAlert.atd != null || preAlert.is_manually_locked;

  const helper = new AdminInquiryDetailHelper(bookingInquiry);
  const canEdit = helper.canEdit(user, preAlert);
  const canUnlock = AdminInquiryDetailHelper.canUnlock(user, preAlert);

  const onVesselEditClick = () => {
    foldMessagesEither(
      canEdit,
      (e) => {
        updateAlert.confirm({ message: e.messages });
        throw new Error('Not authorised to lock');
      },
      () => {
        setVesselScheduleFilterOpen(true);
      },
    );
  };

  const onLockClick = async () => {
    if (!preAlert.is_manually_locked) {
      foldMessagesEither(
        canEdit,
        (e) => {
          updateAlert.confirm({ message: e.messages });
          throw new Error('Not authorised to lock');
        },
        () => {},
      );

      if (!preAlert.vessel_schedule) {
        updateAlert.confirm({ message: strings.vesselScheduleVesselScheduleAlert() });
        return;
      }

      if (isVesselScheduleEtdInvalid && !preAlert.is_empty) {
        updateAlert.confirm({ message: strings.vesselScheduleEtdAlert() });
        return;
      }

      if (user.role === 'FCChina') {
        const result = await bookingWillBeMovedAlert.confirm({
          title: strings.lockPreAlert,
          message: (
            <div className={classes.fontChina}>{strings.bookingWillBeMovedToAfterDepart}</div>
          ),
        });

        if (result === 'cancelled') {
          return;
        }
      }

      await patchPreAlert.mutateAsync({
        is_manually_locked: true,
      } as any);
      invalidate();
    } else {
      foldMessagesEither(
        canUnlock,
        (e) => {
          updateAlert.confirm({ message: e.messages });
          throw new Error('Not authorised to lock');
        },
        () => {},
      );

      if (user.role === 'admin') {
        setUnlockAlertOpen(true);
      } else {
        setUnableToUnlockAlertOpen(true);
      }
    }
  };

  const renderUnlockAlert = () => {
    return (
      <ActionableDialog
        open={unlockAlertOpen}
        onClose={() => setUnlockAlertOpen(false)}
        classes={{ paper: classes.dialog }}
      >
        <ActionableDialogTitle>{strings.unlock}</ActionableDialogTitle>
        <ActionableDialogContent style={{ padding: 55 }}>
          {strings.unlockAlert}
        </ActionableDialogContent>
        <ActionableDialogActions justifyContent="center">
          <Button
            color="primary"
            size="small"
            text={strings.unlock}
            shape="round"
            type="button"
            onClick={async () => {
              await patchPreAlert.mutateAsync({
                is_manually_locked: false,
              } as any);

              invalidate();
              setUnlockAlertOpen(false);
            }}
          />
        </ActionableDialogActions>
      </ActionableDialog>
    );
  };

  const renderUnableToUnlockAlert = () => {
    return (
      <ActionableDialog open={unableToUnlockAlertOpen} classes={{ paper: classes.dialog }}>
        <ActionableDialogTitle>{strings.unlock}</ActionableDialogTitle>
        <ActionableDialogContent style={{ padding: 55 }}>
          {strings.unableToUnlock()}
        </ActionableDialogContent>
        <ActionableDialogActions justifyContent="center">
          <Button
            color="primary"
            size="small"
            text={formStrings.ok}
            shape="round"
            type="button"
            onClick={() => setUnableToUnlockAlertOpen(false)}
          />
        </ActionableDialogActions>
      </ActionableDialog>
    );
  };

  const renderVesselScheduleEtdAlert = () => {
    return (
      <Alert severity="error" open={isVesselScheduleEtdInvalid} gutter>
        {strings.vesselScheduleEtdAlert()}
      </Alert>
    );
  };

  return (
    <PreAlertFormContext.Provider value={{ edit, onEdit }}>
      <SpinnerModal open={patchPreAlert.isLoading} />

      {renderUnlockAlert()}
      {renderUnableToUnlockAlert()}
      {renderVesselScheduleEtdAlert()}
      {updateAlert.renderDialog()}
      {bookingWillBeMovedAlert.renderDialog()}

      <Formik
        initialValues={preAlert as t.TypeOf<typeof formValuesType>}
        {...(edit ? {} : { validate })}
        onSubmit={async (values) => {
          await putPreAlertCreate.mutateAsync(values);
          invalidate();
        }}
        enableReinitialize
      >
        <Form>
          <VesselScheduleInput
            name="vessel_schedule"
            open={vesselScheduleFilterOpen}
            onClose={() => setVesselScheduleFilterOpen(false)}
          />

          <LogForm name="form" />

          <SubmittingOverlay />
          <div className={classes.verticalSpace1}>
            <div className={classes.field}>
              <Field
                index={1}
                name="no"
                label="NO"
                displayOnly={true}
                locked={locked}
                hasEditButton={false}
              />
            </div>
            <div className={classes.field}>
              <Field
                index={2}
                name="customer_no"
                label={strings.customerNo}
                locked={locked}
                displayOnly={true}
                disabled
                hasEditButton={false}
              />
            </div>
            <div className={classes.field}>
              <Field index={3} name="refer_number" label="Refer Number" locked={locked} />
            </div>
            <div className={classes.field}>
              <Field
                index={4}
                name="shipper"
                label="Shipper"
                locked={locked}
                displayOnly={true}
                disabled
                hasEditButton={false}
              />
            </div>
            <div className={classes.field}>
              <Field
                index={5}
                name="consignee"
                label="Consignee"
                locked={locked}
                displayOnly={true}
                disabled
                hasEditButton={false}
              />
            </div>
            <div className={classes.field}>
              <Field
                index={6}
                name="house_bl_number"
                label="HBL"
                locked={locked}
                displayOnly={true}
                disabled
                hasEditButton={false}
              />
            </div>
            <div className={classes.field}>
              <Field index={7} name="carrier" label="Carrier" locked={locked} />
            </div>
            <div className={classes.field}>
              <Field
                index={8}
                name="vessel"
                label="Vessel"
                labelNode={
                  <div className={classes.editableLabel}>
                    <span>8. Vessel</span>
                    <Button
                      color="primary"
                      size="small"
                      text={formStrings.choose}
                      style={{ marginTop: -6 }}
                      shape="round"
                      type="button"
                      onClick={onVesselEditClick}
                      disabled={locked && edit}
                    />
                  </div>
                }
                locked={locked}
                disabled
                hasEditButton={false}
              />
            </div>
            <div className={classes.field}>
              <Field
                index={9}
                name="voyage"
                label="Voyage"
                locked={locked}
                disabled
                hasEditButton={false}
              />
            </div>
            <div className={classes.field}>
              <Field
                index={10}
                name="pol"
                label="POL"
                locked={locked}
                disabled
                hasEditButton={false}
              />
            </div>
            <div className={classes.field}>
              <Field
                index={11}
                name="pod"
                label="POD"
                locked={locked}
                disabled
                hasEditButton={false}
              />
            </div>
            <div className={classes.field}>
              <Field
                index={12}
                name="etd"
                label="ETD"
                type={edit ? 'text' : 'date'}
                locked={locked}
                disabled
                hasEditButton={false}
                mapDisplayValue={(v) => (v ? dayjs(v).format('YYYY/MM/DD') : '')}
              />
            </div>
            <div className={classes.field}>
              <Field
                index={13}
                name="eta"
                label="ETA"
                type={edit ? 'text' : 'date'}
                locked={locked}
                disabled
                hasEditButton={false}
                mapDisplayValue={(v) => (v ? dayjs(v).format('YYYY/MM/DD') : '')}
              />
            </div>
            <div className={classes.field}>
              <Field index={14} name="quantity" label="Quantity" locked={locked} />
            </div>
            <div className={classes.field}>
              <WithPackages>
                <Field index={15} name="packages" label="Packages" locked={locked} />
              </WithPackages>
            </div>
            <div className={classes.field}>
              <Field
                index={16}
                name="shipping_mark"
                label="Shipping Mark"
                multiline
                locked={locked}
              />
            </div>
            <div className={classes.field}>
              <Field index={17} name="commodity" label="Commodity" locked={locked} />
            </div>
            <div className={classes.field}>
              <Field
                index={18}
                name="gross_weight"
                label="Gross Weight"
                unit="KGS"
                locked={locked}
              />
            </div>
            <div className={classes.field}>
              <Field index={19} name="measurement" label="Measurement" unit="CBM" locked={locked} />
            </div>
            <div className={classes.field}>
              <WithPaymentsTerms>
                <Field
                  index={20}
                  name="payments_terms"
                  label="Payments Terms"
                  locked={locked}
                  disabled
                  hasEditButton={false}
                  displayOnly={true}
                />
              </WithPaymentsTerms>
            </div>
            <div className={classes.updatedAt}>
              Last updated: {toJST(new Date(preAlert.updated_at))}
            </div>
            <div
              className={clsx(classes.flexInline, classes.itemsCenter, classes.horizontalSpace1)}
            >
              <div style={{ width: '1.75rem' }} />

              {edit ? (
                <>
                  <Button
                    text={strings.export}
                    color="primary"
                    size="small"
                    shape="round"
                    type="button"
                    onClick={onExportClick}
                  />
                  <Button
                    text={strings.history}
                    color="primary"
                    size="small"
                    shape="round"
                    type="button"
                    onClick={onHistoryClick}
                  />

                  <div style={{ flex: 1 }} />

                  <Button
                    text={
                      <>
                        {!preAlert.is_manually_locked && (
                          <>
                            <img src={lockIcon} alt="Lock" style={{ paddingRight: '0.2rem' }} />
                            {strings.lock}
                          </>
                        )}
                        {preAlert.is_manually_locked && (
                          <>
                            <img src={unlockIcon} alt="Unlock" style={{ paddingRight: '0.2rem' }} />
                            {strings.unlock}
                          </>
                        )}
                      </>
                    }
                    color={preAlert.is_manually_locked ? 'warning' : 'primary'}
                    size="small"
                    shape="round"
                    type="button"
                    onClick={onLockClick}
                  />
                </>
              ) : (
                <Button
                  text={preAlert.is_empty ? strings.create : strings.edit}
                  color="primary"
                  size="small"
                  shape="round"
                  type="submit"
                />
              )}
            </div>
          </div>
        </Form>
      </Formik>
    </PreAlertFormContext.Provider>
  );
};
